import React, { useEffect, useRef, useState } from 'react';
import BasicDrawer from '../../components/Drawer';
import { AddUserSchema, channelCreationSchema } from '../../config/Schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { Avatar, Checkbox, Col, Input, Row, Spin } from 'antd';
import LabelWithField from '../../components/LabelWithField';
import { ApiCaller, Colors, Utils } from '../../config';
import LabelWithDate from '../../components/LabelWithDate';
import LabelWithSelect from '../../components/LabelWithSelect';
import moment from 'moment';
import ButtonLoader from '../../components/Button/ButtonLoader';
import dayjs from 'dayjs';
import { FaCamera } from 'react-icons/fa';
import camera from '../../assets/images/camera.png';
import Text from '../../components/Text';
import { getActiveUsers, getEligibiity } from '../../config/getApis';
import { useQuery } from 'react-query';
import useColors from '../../hooks/useColors';
import { Button } from '../../components';
import Basic from '../../components/Avatar/Basic';
import useDebounce from '../../config/Hooks/useDebounce';
import util from '../../config/util';
import { maxSize, minSize } from '../../config/variables';

const UserAddnEditDrawer = ({
    drawerStates,
    onCloseDrawer,
    imageValue,
    setImageValue,
    setFetchChannels
}) => {
    const { colorPrimary } = useColors();
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // const [filteredUsers, setFilteredUsers] = useState(users);
    const [filteredUsersState, setFilteredUsersState] = useState([]);
    const [mediaID, setMediaID] = useState(''); // For the uploaded image ID
    const [img, setImg] = useState(null);
    const [channelName, setChannelName] = useState(''); // For the channel name
    const [readFile, setReadFile] = useState('');

    // filters for searching 
    const [filtersParams, setFiltersParams] = useState({
        search: '',
        page: 1,
        perPage: 10,
        total: 0
    })

    const [refresh, setRefresh] = useState(false);
    const [loading, setLoading] = useState(false);
    const [createGrpLoader, setCreateGrpLoader] = useState(false);

    const [selectAllActive, setSelectAllActive] = useState(false);
    const [paginateInfo, setPaginateInfo] = useState(null);
    const selectAllActiveRef = useRef(false);

    const debounceValue = useDebounce(filtersParams?.search, 2000);

    useEffect(() => {
        selectAllActiveRef.current = selectAllActive;
    }, [selectAllActive]);

    // keep the react-hook-form field value in sync, including auto-checked users from pagination
    useEffect(() => {
        setValue('userId', selectedUsers);
    }, [selectedUsers])

    // calling an api.
    useEffect(() => {
        if (filtersParams) {
            const fetchusers = async () => {
                try {
                    setLoading(true)
                    let { userOptions, resPaginate } = await getActiveUsers(filtersParams);
                    setFilteredUsersState((prev) => {
                        if (filtersParams.page === 1) {
                            return userOptions || [];
                        }
                        const existingIds = new Set(prev.map((u) => u.value));
                        const newUsers = (userOptions || []).filter((u) => !existingIds.has(u.value));
                        return [...prev, ...newUsers];
                    });
                    // if select all was active, newly loaded users should also come pre-checked
                    if (selectAllActiveRef.current && userOptions?.length) {
                        setSelectedUsers((prev) => [...new Set([...prev, ...userOptions.map((u) => u.value)])]);
                    }
                    setPaginateInfo(resPaginate);
                    setFiltersParams((prev) => ({ ...prev, total: resPaginate?.total }))
                    setLoading(false)
                } catch (error) {
                    console.log(error)
                }
                finally {
                    setLoading(false)
                }
            }
            fetchusers()
        }
    }, [refresh, debounceValue])

    const hasMoreUsers = paginateInfo ? paginateInfo.currentPage < paginateInfo.lastPage : true;

    // infinite scroll pagination, same behaviour as User Management listing
    useEffect(() => {
        const container = document.querySelector('.ant-drawer-body');
        if (!container) return;

        const handleScroll = () => {
            const isNearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
            if (isNearBottom && !loading && hasMoreUsers) {
                setFiltersParams((prev) => ({ ...prev, page: prev.page + 1 }));
                setRefresh((prev) => !prev);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loading, hasMoreUsers, drawerStates?.openDrawer])


    const schema = channelCreationSchema;

    // Form related Working
    const { handleSubmit, control, reset, clearErrors, setValue } = useForm({
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    useEffect(() => {
        let fileReader,
            isCancel = false;

        fileReader = new FileReader();
        if (img) {
            fileReader.onload = (e) => {
                setReadFile(e?.target?.result);
            };
            fileReader.readAsDataURL(img);
        }

        return () => {
            isCancel = true;
            if (fileReader && fileReader.readyState === 1) {
                fileReader.abort();
            }
        };
    }, [img]);


    const handleImageUpload = async (file) => {
        if (!file) return;

        let formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'profile'); // Image upload type

        setIsUploading(true); // Show loader

        try {
            const mediaRes = await ApiCaller.Post('/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (mediaRes?.data?.status === 200) {
                setMediaID(mediaRes.data.data.id); // Store media ID
            } else {
                console.error(
                    'Failed to upload media:',
                    mediaRes?.data?.message,
                );
            }
        } catch (error) {
            console.error('Error while uploading media:', error);
            util.showMessage('error', error?.response?.data?.message)
        } finally {
            setIsUploading(false); // Hide loader
        }
    };

    const fileHandler = (e) => {
        const file = e?.target.files[0];

        if (
            file &&
            file.size >= minSize &&
            file.size <= maxSize &&
            ['image/png', 'image/jpeg'].includes(file?.type)
        ) {
            setImg(file);
            setValue('userimg', file);
            setImageValue({
                imgSizeValidation: '',
                imgTypeValidation: '',
                isRequired: '',
            });
            handleImageUpload(file)
            return;
        }

        else {
            if (file?.size < minSize || file?.size > maxSize) {
                setImageValue({
                    isRequired: '',
                    imgTypeValidation: '',
                    imgSizeValidation: 'Image size must be between 1KB and 6MB',
                });
                setImg('');
                setValue('userimg', null);
                setReadFile('');
                return;
            }
            else if (!['image/png', 'image/jpeg'].includes(file?.type)) {
                setImageValue({
                    isRequired: '',
                    imgSizeValidation: '',
                    imgTypeValidation: 'Type should be either jpeg or png',
                });
                setImg('');
                setValue('userimg', null);
                setReadFile('');
                return;
            }
        }
    };

    const handleButtonClick = async (data) => {
        console.log(data)

        const payload = {
            channelName: data?.channelName,
            userId: data?.userId,
            imageId: mediaID,
        };

        try {
            // Make API request using axios
            setCreateGrpLoader(true)
            const response = await ApiCaller.Post(
                '/admin/chat-channel',
                payload,
            );

            // Show success message
            Utils.showMessage('success', response?.data?.message);

            onCloseDrawer();
            setImg(null);
            setReadFile('');
            setSelectedUsers([])
            setSelectAllActive(false);
            reset({
                channelName: '',
                userId: []
            });
            setFetchChannels(true);
        } catch (error) {
            // Show error message
            Utils.showMessage(
                'error',
                error?.response?.data?.message || 'An error occurred',
            );
            // onCloseDrawer();
        }
        finally {
            setCreateGrpLoader(false)
        }
    };


    return (
        <BasicDrawer
            openDrawer={drawerStates?.openDrawer}
            // afterOpenChange={(isOpen) => drawerKeyHandler(isOpen, reset)}
            title={
                drawerStates?.drawerkey == 1
                    ? 'Edit User'
                    : 'Create New Channel'
            }
            onClose={() => {
                reset({
                    channelName: '',
                    userId: []
                });
                setImg(null);
                setReadFile('');
                setSelectedUsers([]);
                setSelectAllActive(false);
                onCloseDrawer()
            }
            }
            width={600}
        >
            <Row className="w-full">
                <Row className="w-full flex justify-between">
                    <form className='w-full'>
                        <Row
                            className="py-5 w-full flex items-center justify-center relative p-2.5 h-24"
                        >
                            <Col className="flex items-center justify-center relative w-16 h-16 rounded-full border border-[#F9F9FA] bg-[#F9F9FA]">
                                <Controller
                                    control={control}
                                    name="userimg"
                                    defaultValue={''}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <>
                                                <input
                                                    type="file"
                                                    className="absolute inset-0 rounded-full w-full h-full z-[999] opacity-0"
                                                    onChange={(e) => {
                                                        fileHandler(e);
                                                    }}
                                                    accept=".png, .jpg, .jpeg"
                                                />
                                            </>
                                        </>
                                    )}
                                />
                                {readFile ? (
                                    <>
                                        <Avatar
                                            className="z-10 absolute w-full h-full flex items-center justify-center"
                                            src={readFile}
                                        />
                                        {
                                            isUploading ?
                                                <div className='absolute z-20 right-0 bottom-0'>
                                                    <Spin />
                                                </div>
                                                :
                                                <FaCamera
                                                    className="absolute z-20 right-0 bottom-0"
                                                    size={20} // Set the size of the icon (similar to width and height of the img tag)
                                                    style={{
                                                        color: colorPrimary,
                                                    }} // You can adjust the color or style if needed
                                                />
                                        }
                                    </>
                                ) : (
                                    <Avatar
                                        className="z-0 absolute w-full h-full flex items-center justify-center"
                                        icon={<FaCamera />}
                                    />
                                )}
                            </Col>

                            {/* user image validation  */}
                            <p className="text-center text-red-600 w-[100%]">
                                {imageValue && imageValue.imgSizeValidation ? (
                                    <p>{imageValue.imgSizeValidation}</p>
                                ) : imageValue && imageValue.imgTypeValidation ? (
                                    <p>{imageValue?.imgTypeValidation}</p>
                                )
                                    : (
                                        ''
                                    )}
                            </p>
                        </Row>

                        <Row className="w-full gap-2">
                            <Col span={10}>
                                <Controller
                                    control={control}
                                    name="channelName" // channelName should come from firstName
                                    render={({ field, fieldState }) => (
                                        <>
                                            <LabelWithField
                                                labeltext={'Channel Name'}
                                                field={field}
                                                type={'text'}
                                                labelClass={'font-normal'}
                                                inputClass={
                                                    'outline-none h-10 rounded'
                                                }
                                                onChange={(e) => {
                                                    field.onChange(e); // Ensure react-hook-form updates the field
                                                    setChannelName(e.target.value); // Update local state
                                                }}
                                            />

                                            {fieldState?.error && (
                                                <p className="text-red-600">
                                                    {fieldState?.error.message}
                                                </p>
                                            )}
                                        </>
                                    )}
                                />
                            </Col>
                            <Col span={10} className="mt-10">
                                {/* Search Bar */}
                                <Input
                                    placeholder="Search users by name"
                                    value={filtersParams?.search}
                                    onChange={(e) =>
                                        // setSearchText(e.target.value)
                                        setFiltersParams((prev) => ({ ...prev, search: e?.target?.value, page: 1 }))
                                    } // Update searchText state
                                    className="mb-4"
                                />
                            </Col>
                        </Row>

                        <Row className="w-6/12 h-2"></Row>

                        <Row className="w-full mt-4">
                            {
                                loading && filteredUsersState?.length === 0 ?
                                    <p className='text-center w-full'>
                                        <Spin />
                                    </p>
                                    :
                                    <Col span={24}>
                                        <div className="space-y-4">
                                            {/* User List */}
                                            <div className="text-sm">
                                                Select users to add:
                                            </div>
                                            <Controller
                                                control={control}
                                                name='userId'
                                                defaultValue={[]}
                                                render={({ field, fieldState }) => {
                                                    const allSelected = filteredUsersState?.length > 0 && filteredUsersState.every(u => selectedUsers.includes(u.value));
                                                    const someSelected = filteredUsersState?.some(u => selectedUsers.includes(u.value));

                                                    const handleSelectAll = () => {
                                                        let updatedSelection;
                                                        if (allSelected) {
                                                            setSelectAllActive(false);
                                                            updatedSelection = selectedUsers.filter(id => !filteredUsersState.some(u => u.value === id));
                                                        } else {
                                                            setSelectAllActive(true);
                                                            const currentPageIds = filteredUsersState.map(u => u.value);
                                                            updatedSelection = [...new Set([...selectedUsers, ...currentPageIds])];
                                                        }
                                                        setSelectedUsers(updatedSelection);
                                                        field.onChange(updatedSelection);
                                                    };

                                                    return (
                                                        filteredUsersState && filteredUsersState?.length > 0 ? (
                                                            <>
                                                                <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
                                                                    <Checkbox
                                                                        checked={allSelected}
                                                                        indeterminate={someSelected && !allSelected}
                                                                        onChange={handleSelectAll}
                                                                    />
                                                                    <span className="ml-2 font-medium text-sm">Select All</span>
                                                                    {selectedUsers.length > 0 && (
                                                                        <span className="ml-auto text-xs text-gray-500">{selectedUsers.length} selected</span>
                                                                    )}
                                                                </div>
                                                                {
                                                                    filteredUsersState.map((user) => (
                                                                        <div
                                                                            key={user.value}
                                                                            className="flex items-center"
                                                                        >
                                                                            <Checkbox
                                                                                checked={selectedUsers.includes(
                                                                                    user.value,
                                                                                )}
                                                                                onChange={() => {
                                                                                    const isRemoving = selectedUsers.includes(user.value);
                                                                                    const updatedSelection = isRemoving
                                                                                        ? selectedUsers.filter((id) => id !== user.value)
                                                                                        : [...selectedUsers, user.value];
                                                                                    if (isRemoving) {
                                                                                        setSelectAllActive(false);
                                                                                    }
                                                                                    field?.onChange(updatedSelection)
                                                                                    setSelectedUsers(updatedSelection)
                                                                                }}
                                                                            />
                                                                            <span className="ml-2">
                                                                                {user.label}
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                }
                                                                {fieldState?.error && (
                                                                    <p className="text-red-600">
                                                                        {fieldState?.error.message}
                                                                    </p>
                                                                )}
                                                            </>
                                                        )
                                                            : (
                                                                <p>No users found.</p>
                                                            )
                                                    )
                                                }}

                                            />
                                            {
                                                loading && filteredUsersState?.length > 0 &&
                                                <div className='flex items-center justify-center py-2'>
                                                    <Spin size="small" />
                                                </div>
                                            }
                                        </div>
                                    </Col>
                            }

                        </Row>

                        <Row className="w-full flex justify-end">
                            <ButtonLoader
                                btnHandler={handleSubmit(handleButtonClick)} // Call handleButtonClick on click
                                loader={createGrpLoader}
                                className={
                                    'cursor-pointer my-2 mx-1 h-10 rounded-none px-8 bg-[#FF4D4F] text-white'
                                }
                                isDisabled={isUploading}
                                style={{ backgroundColor: colorPrimary }}
                                text={'Create'}
                            />
                        </Row>
                    </form>
                </Row>
            </Row>
        </BasicDrawer >
    );
};

export default UserAddnEditDrawer;
